import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import API from '../../../utils/API';
import RadioCheck from '../../../components/MessageManager/RadioCheck';
import './GroupManagement.css';

const AddNewGroup = ({
  groupsTreeData,
  setSuccess,
  closeModal,
}) => {

  const [formData, setFormData] = useState({
    groupName: '',
    parentId: '',
  });
  const groupTypeOptions = { "new": "NEW_GROUP", "sub": "SUB_GROUP" };
  const [modalError, setModalError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const intl = useIntl();
  const [groupType, setGroupType] = useState('new');
  const t = (id) => intl.formatMessage({ id });
  const [groupData, setGroupData] = useState([]);

  /**
    * Reset modal
    */
  function resetModal() {
    setSubmitted(false);
    setModalError('');
    setFormData({
      groupName: '',
      parentId: '',
    });
  }

  useEffect(() => {
    setGroupData(groupsTreeData);
    if (groupsTreeData.length > 0) {
      setFormData((prevData) => ({
        ...prevData,
        parentId: groupsTreeData[0].key,
      }));
    }
  }, [groupsTreeData]);

  useEffect(() => {
    const modalElement = document.getElementById('groupModal');
    modalElement.addEventListener('hidden.bs.modal', resetModal);

    return () => {
      modalElement.removeEventListener('hidden.bs.modal', resetModal);
    };
  }, []);

  /**
   * Handle form input change
   */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === 'groupName' && (value.length < 2 || value.length > 36)) {
      setModalError(<FormattedMessage id="GROUP_NAME_LENGTH" />);
    } else {
      setModalError('');
    }
  }, []);

  /**
   * Add new group to the database
   */
  const handleAddNewGroup = async () => {
    const groupData = {
      groupName: formData.groupName.trim(),
      parentId: groupType === "new" ? null : formData.parentId,
    };
    if (groupData.groupName.length < 2 || groupData.groupName.length > 36) {
      setModalError(<FormattedMessage id="GROUP_NAME_LENGTH" />);
      return;
    }
    try {
      await API.insertGroup(groupData);
      setSuccess(<FormattedMessage id="ADD_GROUP_SUCCESS" />);
      closeModal("groupModal");
      resetModal();
    } catch (err) {
      const errorMessage = err.response?.data?.errorId ? t(err.response.data["errorId"]) : err.message;
      setModalError(<FormattedMessage id="ADD_GROUP_FAILED" values={{ errorMsg: <b>{errorMessage}</b> }} />);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (formData.groupName && (groupType === 'new' || (groupType === 'sub' && formData.parentId))) {
      handleAddNewGroup();
    } else {
      setModalError(<FormattedMessage id="SUBMIT_ERROR" />);
    }
  };

  return (
    <div className='modal modal-mse fade' id='groupModal' tabIndex='-1' aria-labelledby='groupModalLabel' aria-hidden='true'>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content modal-mes-content'>
          <div className='modal-header modal-mes-header' style={{ backgroundColor: '#347BFA' }}>
            <h5 className='modal-title modal-mes-title'><FormattedMessage id='ADD_NEW_GROUP' /></h5>
            <button id="closeModal" type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Close'></button>
          </div>
          <div className='modal-body'>
            <form onSubmit={handleSubmit}>
              <div className='row'>
                <div className='groupTypeSection mb-4 d-none'>
                  <label className="col-form-label mes-from-label-2"><FormattedMessage id="SELECT_MESSAGE_STYLE" /></label>
                  <RadioCheck
                    options={groupTypeOptions}
                    name="groupType"
                    selectedOption={groupType}
                    disabled="disabled"
                    onChange={(e) => setGroupType(e.target.value)}
                  />
                </div>
                {groupType === 'sub' && (
                  <div className='parentGroupNameSection mb-2'>
                    <label htmlFor='parentGroupNameLabel' className='col-form-label mes-from-label-2'>
                      <FormattedMessage id='PARENT_GROUP_NAME' />:
                    </label>
                    <select
                      id="parentGroupNameSelect"
                      onChange={handleChange}
                      value={formData.parentId}
                      name='parentId'
                      className={`form-select h-54 bg-baby-blue mb-3 ${submitted && (!formData.parentId ? 'is-invalid' : 'is-valid')}`}
                      aria-label="example"
                    >
                      {groupData.map(group => (
                        <option key={group.key} value={group.key}>{group.title}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className='groupNameSection mb-2'>
                  <label htmlFor='groupNameLabel' className='col-form-label mes-from-label-2'>
                    <FormattedMessage id={groupType === 'new' ? 'GROUP_NAME' : 'SUB_GROUP_NAME'} />:
                  </label>
                  <input
                    id="groupNameInput"
                    onChange={handleChange}
                    value={formData.groupName}
                    name='groupName'
                    placeholder={t(groupType === 'new' ? 'GROUP_NAME' : 'SUB_GROUP_NAME')}
                    className={`form-control h-54 bg-baby-blue ${submitted && (modalError ? 'is-invalid' : 'is-valid')}`}
                  />
                </div>
                {submitted && modalError && (
                  <div className='modal-error invalid-feedback d-block fw-bold text-center'>
                    {modalError}
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className='modal-footer modal-mes-footer d-flex align-items-center'>
            <button id="backGroupModalBtn" type='button' className='btn btn-outline-secondary me-3' data-bs-dismiss='modal'>
              <FormattedMessage id='CANCEL' />
            </button>
            <button id="saveGroupBtn" className='btn btn-primary but-darkblue' type='button' onClick={handleSubmit}>
              <FormattedMessage id='ADD_NEW_GROUP' />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNewGroup;
